import { useEffect, useState } from "react";
import WifiOff from "@mui/icons-material/WifiOff";
import { ConnectionDialog } from "./ConnectionDialog";
import { useTranslation } from "react-i18next";

/**
 * This dialog is used to display a message to the user when the app is offline.
 * It is used to inform the user that the app is offline and that they should check their internet connection.
 * It is also used to reload the app when the user is offline and comes back online.
 * @returns A dialog with a message to the user when the app is offline.
 */
export const OfflineDetectorDialog = () => {
  const { t } = useTranslation();
  const [isOnline, setIsOnline] = useState(true);
  const [hadOfflineIssue, setHadOfflineIssue] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      if (hadOfflineIssue) {
        window.location.reload();
      }
      setIsOnline(true);
      setHadOfflineIssue(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setHadOfflineIssue(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [hadOfflineIssue]);

  if (isOnline) {
    return null;
  }

  return (
    <ConnectionDialog
      content={{
        icon: <WifiOff fontSize="large" />,
        title: t("noInternetConnection"),
        severity: "error" as const,
        bgcolor: "error.main",
        color: "error.contrastText",
        message: t("thisAppRequiresAnInternetConnection"),
        subtitle: t("pleaseCheckYourInternetConnection"),
        buttonBgColor: "error.contrastText",
        buttonTextColor: "error.main",
      }}
    />
  );
};
